import React, {
  useState,
  useMemo,
  useCallback
} from "react";
import {
  ScrollView,
  useWindowDimensions,
  View,
  Platform,
  StyleSheet,
  LayoutChangeEvent,
} from "react-native";
import {
  useSelector
} from "react-redux";
import {
  DefaultRootState
} from "@reduxLocal/persistState";
import {
  useDeviceType
} from "@hooks/useDeviceType";
import {
  useQuery
} from "@tanstack/react-query";
import {
  DraggableGrid
} from "react-native-draggable-grid";
import apis from "@api/api";
import {
  WidgetWrapper,
  WelcomeHeaderWidget,
  StatCard,
  DataChartWidget,
  UsageStatWidget,
  UsageDonutWidget,
  ActivityLineChartWidget,
  UsageDistributionPieChartWidget,
  ProgressChartWidget,
  MonthlyBarChartWidget,
  PerformanceWidget,
  SubscriptionWidget,
  WidgetKey,
  WidgetSize,
  SIZE_CONFIG,
  DEFAULT_WIDGET_ORDER,
} from ".";

const cycleSize = (size: WidgetSize): WidgetSize => {
  if (size === "small") return "medium";
  if (size === "medium") return "large";
  return "small";
};

const WelcomeWidget = () => {
  const {
    userData
  } = useSelector(
    (state: DefaultRootState) => state.promiseData?.data
  );
  const messageCount =
    useSelector((state: DefaultRootState) => state.UI.counts?.messageCount) ||
    0;
  const invitationCount =
    useSelector(
      (state: DefaultRootState) => state.UI.counts?.invitationCount
    ) || 0;
  const calendarCount =
    useSelector((state: DefaultRootState) => state.UI.counts?.calendarCount) ||
    0;
  const {
    isTablet
  } = useDeviceType();

  const [containerWidth, setContainerWidth] = useState(0);

  const [widgetOrder, setWidgetOrder] =
    useState<WidgetKey[]>(DEFAULT_WIDGET_ORDER);
  const [widgetSizes, setWidgetSizes] = useState<Record<WidgetKey,
    WidgetSize>>
    (() =>
      DEFAULT_WIDGET_ORDER.reduce((acc, key) => {
        const defaultSize: WidgetSize =
          key === "messages" ||
            key === "calendar" ||
            key === "invitations" ||
            key === "team" ?
            "small" :
            key === "performance" ?
              "large" :
              "medium";
        return {
          ...acc,
          [key]: defaultSize
        };
      }, {} as Record<WidgetKey, WidgetSize>)
    );

  const {
    data: statData,
    isLoading: statLoading
  } = useQuery({
    queryKey: ["statCount"],
    queryFn: async () => await apis.getStatCounts(),
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: companyMembers = []
  } = useQuery({
    queryKey: ["companyMembers"],
    queryFn: async () => {
      const data = await apis.getUserHierarchy();
      return data?.data || [];
    },
    staleTime: 1000 * 60 * 60,
  });

  const {
    data: subscriptionData
  } = useQuery({
    queryKey: ["subscriptionPlan"],
    queryFn: async () => await apis.getSubscriptions(),
    staleTime: 1000 * 60 * 60 * 5,
  });

  const numColumns = useMemo(() => {
    if (containerWidth < 850) return 1;
    if (containerWidth > 1400) return 3;
    return 2;
  }, [containerWidth]);

  const gridData = useMemo(
    () => widgetOrder.map((key) => ({
      key,
      size: widgetSizes[key]
    })), [widgetOrder, widgetSizes]
  );

  const handleDragRelease = useCallback(
    (items: Array<{
      key: string
    }>) => {
      setWidgetOrder(items.map((item) => item.key as WidgetKey));
    }, []
  );

  const handleResize = useCallback((key: WidgetKey) => {
    setWidgetSizes((prev) => ({
      ...prev,
      [key]: cycleSize(prev[key] ?? "medium"),
    }));
  }, []);

  const getChartDimension = useCallback(
    (size: WidgetSize) => {
      if (size === "small") return {
        width: isTablet ? 260 : 240,
        height: 180
      };
      if (size === "large") return {
        width: isTablet ? 380 : 340,
        height: 260
      };
      return {
        width: isTablet ? 320 : 280,
        height: 220
      };
    }, [isTablet]
  );

  const renderWidgetContent = useCallback(
    (key: WidgetKey, size: WidgetSize) => {
      const dimension = getChartDimension(size);
      switch (key) {
        case "welcome":
          return (<
            WelcomeHeaderWidget userName={
              userData?.name || "User"
            }
          />
          );
        case "messages":
          return <StatCard widgetKey="messages"
            value={
              messageCount
            }
          />;
        case "calendar":
          return <StatCard widgetKey="calendar"
            value={
              calendarCount
            }
          />;
        case "invitations":
          return (<
            StatCard widgetKey="invitations"
            value={
              invitationCount
            }
          />
          );
        case "team":
          return (<
            StatCard widgetKey="team"
            value={
              companyMembers.length
            }
          />
          );
        case "dataChart":
          return <DataChartWidget data={
            statData?.data || 20
          }
          />;
        case "usageStat":
          return <UsageStatWidget />;
        case "usageDonut":
          return <UsageDonutWidget />;
        case "activityLine":
          return (<
            ActivityLineChartWidget isLoading={
              statLoading
            }
            width={
              dimension.width
            }
            height={
              dimension.height
            }
          />
          );
        case "usagePie":
          return (<
            UsageDistributionPieChartWidget data={
              {
                companyMembers: companyMembers.length,
                projects: statData?.projects || 5,
                models: statData?.models || 3,
                aiCalls: statData?.aiCall || 25,
              }
            }
            isLoading={
              statLoading
            }
            width={
              dimension.width
            }
            height={
              dimension.height
            }
          />
          );
        case "progress":
          return <ProgressChartWidget isLoading={
            statLoading
          }
          />;
        case "monthly":
          return (<
            MonthlyBarChartWidget data={
              statData?.data || 20
            }
            isLoading={
              statLoading
            }
            width={
              dimension.width
            }
            height={
              dimension.height
            }
          />
          );
        case "performance":
          return <PerformanceWidget />;
        case "subscription":
          return <SubscriptionWidget subscriptionData={
            subscriptionData
          }
          />;
        default:
          return null;
      }
    }, [
    getChartDimension,
    userData?.name,
    messageCount,
      calendarCount,
      invitationCount,
      companyMembers.length,
      statData,
      statLoading,
      subscriptionData,
    ]
  );

  const renderItem = useCallback(
    (item: {
      key: string; size: WidgetSize
    }) => {
      const widgetKey = item.key as WidgetKey;
      const size = item.size ?? "medium";
      const height = SIZE_CONFIG[size];
      const isMobile = !isTablet && Platform.OS !== "web";

      return (
        <WidgetWrapper
          widgetKey={widgetKey}
          size={size}
          onResize={handleResize}
          isMobile={isMobile}
          height={height}
        >
          {renderWidgetContent(widgetKey, size)}
        </WidgetWrapper>
      );
    }, [handleResize, renderWidgetContent, isTablet]
  );

  const onLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };

  return (
    <ScrollView style={
      styles.container
    }
      showsVerticalScrollIndicator={
        false
      } >
      <
    View
        style={styles.gridWrapper}
        onLayout={onLayout}
    >
        <
          DraggableGrid data={
            gridData
          }
          renderItem={
            renderItem
          }
          numColumns={
            numColumns
          }
          onDragRelease={
            handleDragRelease
          }
          key={
            `${numColumns}-${gridData.length}`
          }
        /> <
    /View> <
    /ScrollView>
  );
};

        const styles = StyleSheet.create({
          container: {
          flex: 1,
        paddingHorizontal: Platform.OS === "web" ? 10 : 20,
  },
  gridWrapper: {
    flex: 1,
        paddingVertical: 20,
  },
});

        export default WelcomeWidget;